import React, { useEffect } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { Header, Footer } from "../components/SiteLayout";
import {
  Smartphone,
  Copy,
  CheckCircle2,
  CalendarDays,
  Clock,
  MapPin,
  ArrowLeft,
  Mail,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const SWISH_NUMBER = "076-1479408";

const DAY_NAMES = [
  "söndag",
  "måndag",
  "tisdag",
  "onsdag",
  "torsdag",
  "fredag",
  "lördag",
];

const SLOT_LABELS = {
  morgon: "Morgon (08:00–12:00)",
  eftermiddag: "Eftermiddag (12:00–16:00)",
  kvall: "Kväll (16:00–20:00)",
};

const SERVICE_LABELS = {
  grasklippning: "Gräsklippning och trimmning",
  hacktrimning: "Häcktrimning",
  biltvatt: "Biltvätt",
};

const HEDGE_LABELS = {
  mini: "Mini (under 2 lm)",
  liten: "Liten (2–5 lm)",
  mellan: "Mellan (5–15 lm)",
  stor: "Stor (15–25 lm)",
  jatte: "Jätte (över 25 lm)",
};

function formatISO(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAY_NAMES[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
}

function visitDates(start, isSubscription) {
  if (!start) return [];
  const [y, m, d] = start.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  const count = isSubscription ? 4 : 1;
  return Array.from({ length: count }, (_, i) => {
    const dt = new Date(base);
    dt.setDate(base.getDate() + i * 7);
    return dt;
  });
}

function formatDt(dt) {
  return `${DAY_NAMES[dt.getDay()]} ${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
}

export default function PaymentPage() {
  const location = useLocation();
  const booking = location.state?.booking;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  if (!booking) {
    return <Navigate to="/boka" replace />;
  }

  const copySwish = async () => {
    try {
      await navigator.clipboard.writeText(SWISH_NUMBER.replace(/\D/g, ""));
      toast.success("Swish-nummer kopierat!");
    } catch {
      toast.error("Kunde inte kopiera. Skriv ner numret manuellt.");
    }
  };

  const dates = visitDates(booking.booking_date, booking.subscription);

  // Service summary lines
  const serviceLines = booking.services.map((s) => {
    let label = SERVICE_LABELS[s] || s;
    if (s === "grasklippning" && booking.lawn_sqm) {
      label += ` — ${booking.lawn_sqm} kvm`;
    } else if (s === "hacktrimning" && booking.hedge_size) {
      label += ` — ${HEDGE_LABELS[booking.hedge_size]}`;
    }
    return label;
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-page">
      <Toaster position="top-center" richColors />
      <Header />

      <main className="pt-24 sm:pt-28 pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          {/* Confirmation banner */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-100 text-green-700">
              <CheckCircle2 size={28} />
            </div>
            <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Tack, {booking.name}!
            </h1>
            <p className="mt-3 text-slate-600 max-w-md mx-auto leading-relaxed">
              Din bokningsförfrågan är mottagen. Slutför med betalning via Swish
              nedan så bekräftar vi dagen.
            </p>
          </div>

          {/* Payment card */}
          <div className="mt-10 rounded-3xl bg-gradient-to-br from-green-600 to-green-700 text-white p-8 sm:p-10 shadow-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-semibold tracking-wider uppercase">
                  <Smartphone size={14} /> Swish-betalning
                </div>
                <div className="mt-3 text-sm uppercase tracking-wider text-green-100 font-semibold">
                  Belopp att betala
                </div>
                <div
                  className="mt-1 text-5xl sm:text-6xl font-extrabold"
                  data-testid="payment-amount"
                >
                  {booking.estimated_price} kr
                </div>
                {booking.subscription && (
                  <div className="mt-2 text-sm text-green-50">
                    Inkluderar 15% abonnemangsrabatt (4 besök)
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8 rounded-2xl bg-white text-slate-900 p-5 sm:p-6">
              <div className="text-xs font-semibold uppercase tracking-wider text-green-700">
                Swisha till
              </div>
              <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
                <div
                  className="text-3xl sm:text-4xl font-extrabold tabular-nums tracking-tight"
                  data-testid="swish-number"
                >
                  {SWISH_NUMBER}
                </div>
                <button
                  type="button"
                  onClick={copySwish}
                  data-testid="copy-swish-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold transition-colors"
                >
                  <Copy size={16} /> Kopiera
                </button>
              </div>
              <div className="mt-4 text-xs text-slate-500 leading-relaxed">
                Använd ditt fulla namn ({booking.name}) som meddelande i Swish så
                att vi enkelt kan matcha betalningen mot din bokning.
              </div>
            </div>

            <ol className="mt-6 grid gap-3 sm:grid-cols-3 text-sm">
              {[
                "Öppna Swish-appen",
                `Swisha ${booking.estimated_price} kr till ${SWISH_NUMBER}`,
                "Vi bekräftar via mail när betalningen kommit in",
              ].map((step, i) => (
                <li
                  key={i}
                  className="rounded-xl bg-white/10 backdrop-blur p-3 leading-snug"
                >
                  <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white text-green-700 text-xs font-bold mb-1.5">
                    {i + 1}
                  </div>
                  <div>{step}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* Booking summary */}
          <section
            className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8"
            data-testid="payment-booking-summary"
          >
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
              Din bokning
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Boknings-ID: {booking.id}
            </p>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <InfoRow icon={CalendarDays} label="Besök">
                <ul className="space-y-1">
                  {dates.map((d, i) => (
                    <li key={i} className="text-sm text-slate-800">
                      {dates.length > 1 && (
                        <span className="inline-block w-5 text-green-700 font-bold">
                          {i + 1}.
                        </span>
                      )}
                      {formatDt(d)}
                    </li>
                  ))}
                </ul>
              </InfoRow>
              <InfoRow icon={Clock} label="Tidsfönster">
                {SLOT_LABELS[booking.time_slot] || booking.time_slot}
              </InfoRow>
              <InfoRow icon={MapPin} label="Adress">
                {booking.address}
              </InfoRow>
              <InfoRow icon={Mail} label="Bekräftelse skickad till">
                {booking.email}
              </InfoRow>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                Tjänster
              </div>
              <ul className="space-y-1.5">
                {serviceLines.map((line, i) => (
                  <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                    <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              {booking.notes && (
                <div className="mt-4 text-sm text-slate-700">
                  <span className="font-semibold">Meddelande:</span>{" "}
                  {booking.notes}
                </div>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200 flex items-end justify-between">
              <div className="text-sm text-slate-700 font-medium">
                {booking.subscription ? "Totalt (4 besök, −15%)" : "Totalt"}
              </div>
              <div className="text-3xl font-extrabold text-slate-900">
                {booking.estimated_price} kr
              </div>
            </div>
          </section>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white ring-1 ring-slate-300 px-6 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              data-testid="back-home-btn"
            >
              <ArrowLeft size={16} /> Till startsidan
            </Link>
            <a
              href={`tel:${SWISH_NUMBER.replace(/\D/g, "")}`}
              className="inline-flex items-center justify-center rounded-full bg-green-600 hover:bg-green-700 px-6 py-3 text-sm font-semibold text-white"
              data-testid="call-business-btn"
            >
              Ring oss om problem uppstår
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function InfoRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-green-50 text-green-700 flex-shrink-0">
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className="text-sm text-slate-900 mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}
